import React, { useEffect, useId, useRef, createContext, useContext } from 'react';
import styles from './Modal.module.scss';
import { ModalBody, ModalBodyProps } from './ModalBody';
import { ModalFooter, ModalFooterProps } from './ModalFooter';
import { ModalHeader, ModalHeaderProps } from './ModalHeader';

// Context for sharing modal state with subcomponents
interface ModalContextType {
  onClose: () => void;
}

export const ModalContext = createContext<ModalContextType | null>(null);

// Main Modal Props
interface GenericModalProps {
  /**
   * Controls whether the modal is visible
   */
  open: boolean;
  
  /**
   * Handler called when the modal should close
   */
  onClose: () => void;
  
  /**
   * Modal content
   */
  children: React.ReactNode;
  
  /**
   * Optional custom class for the modal container
   */
  className?: string;
  
  /**
   * Whether to close the modal when clicking outside of it
   * @default true
   */
  closeOnOutsideClick?: boolean;
  
  /**
   * Whether to close the modal when pressing Escape key
   * @default true
   */
  closeOnEsc?: boolean;
  
  /**
   * Size preset for the modal
   * @default "md"
   */
  size?: 'sm' | 'md' | 'lg' | 'xl' | 'full';
  
  /**
   * Optional custom max width (overrides size)
   */
  maxWidth?: string;
  
  /**
   * Whether to center the modal vertically
   * @default true
   */
  centered?: boolean;
  
  /**
   * Optional custom z-index
   */
  zIndex?: number;
}

/**
 * A composable modal component with Header, Body, and Footer subcomponents.
 * Can be used either as a complete component or with its subcomponents.
 */
const GenericModal: React.FC<GenericModalProps> & {
  Header: React.FC<ModalHeaderProps>;
  Body: React.FC<ModalBodyProps>;
  Footer: React.FC<ModalFooterProps>;
} = ({
  open,
  onClose,
  children,
  className = '',
  closeOnOutsideClick = true,
  closeOnEsc = true,
  size = 'md',
  maxWidth,
  centered = true,
  zIndex,
}) => {
  const id = useId();
  const modalRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  
  // Handle modal visibility and animations
  useEffect(() => {
    const modalElement = modalRef.current;
    const contentElement = contentRef.current;
    
    if (!modalElement || !contentElement) return;
    
    if (open) {
      // Show modal with animation
      document.body.style.overflow = 'hidden';
      modalElement.classList.add(styles.visible);
      setTimeout(() => {
        contentElement.classList.add(styles.visible);
      }, 50);
    } else {
      // Hide with animation
      document.body.style.overflow = '';
      contentElement.classList.remove(styles.visible);
      setTimeout(() => {
        modalElement.classList.remove(styles.visible);
      }, 300);
    }
    
    return () => {
      document.body.style.overflow = '';
    };
  }, [open]);
  
  // Handle click outside modal content
  useEffect(() => {
    if (!closeOnOutsideClick) return;
    
    const handleOutsideClick = (e: MouseEvent) => {
      if (modalRef.current === e.target) {
        onClose();
      }
    };
    
    window.addEventListener('click', handleOutsideClick);
    return () => window.removeEventListener('click', handleOutsideClick);
  }, [closeOnOutsideClick, onClose]);
  
  // Handle ESC key press
  useEffect(() => {
    if (!closeOnEsc) return;
    
    const handleKeyPress = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    
    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [closeOnEsc, onClose]);

  // Get size class
  const getSizeClass = () => {
    switch(size) {
      case 'sm': return styles.sizeSm;
      case 'md': return styles.sizeMd;
      case 'lg': return styles.sizeLg;
      case 'xl': return styles.sizeXl;
      case 'full': return styles.sizeFull;
      default: return styles.sizeMd;
    }
  };
  
  return (
    <ModalContext.Provider value={{ onClose }}>
      <div 
        id={id} 
        className={styles.modalBackdrop} 
        ref={modalRef}
        aria-modal="true"
        role="dialog"
        style={zIndex ? { zIndex } : undefined}
        data-testid="modal-backdrop"
      >
        <div 
          className={`${styles.modalContainer} ${getSizeClass()} ${centered ? styles.centered : ''} ${className}`}
          ref={contentRef}
          style={maxWidth ? { maxWidth } : undefined}
          data-testid="modal-container"
        >
          {children}
        </div>
      </div>
    </ModalContext.Provider>
  );
};

// Assign subcomponents
GenericModal.Header = ModalHeader;
GenericModal.Body = ModalBody;
GenericModal.Footer = ModalFooter;

export default GenericModal;
